import { useEffect, useMemo, useState } from 'react';
import { useSession } from 'next-auth/react';
import { SESSION_STATUS } from '@/constants/auth';
import { useI4Links } from '@/publicSearch/graphql/i4/hooks';

export type SocietyAccessByCode = {
  exists: boolean;
  isPrivate: boolean;
  canAccess: boolean;
  societyId: number;
  societyCode: string;
  name: string;
};

export function useSocietyAccessByCode(societyCode: string) {
  const { status: sessionStatus } = useSession();
  const sessionReady = sessionStatus !== SESSION_STATUS.LOADING;
  const isAuthenticated = sessionStatus === SESSION_STATUS.AUTHENTICATED;

  const [access, setAccess] = useState<SocietyAccessByCode | null>(null);
  const [loading, setLoading] = useState(Boolean(societyCode));
  const [error, setError] = useState<Error | null>(null);

  const normalizedCode = societyCode.trim().toUpperCase();
  const { data: linksData, loading: linksLoading } = useI4Links(
    !sessionReady || !isAuthenticated || !normalizedCode,
  );

  const hasLinkForSociety = useMemo(
    () =>
      (linksData?.i4Links ?? []).some(
        (link) => link.societyCode?.trim().toUpperCase() === normalizedCode,
      ),
    [linksData?.i4Links, normalizedCode],
  );

  useEffect(() => {
    if (!societyCode) {
      setAccess(null);
      setLoading(false);
      setError(null);
      return;
    }

    if (!sessionReady) {
      setLoading(true);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    const encoded = encodeURIComponent(societyCode.trim());
    fetch(`/api/publicSearch/society-access/${encoded}`, {
      cache: 'no-store',
      credentials: 'same-origin',
    })
      .then(async (res) => {
        if (!res.ok) {
          const text = await res.text();
          throw new Error(text || `Access check failed (${res.status})`);
        }
        return res.json() as Promise<SocietyAccessByCode>;
      })
      .then((data) => {
        if (!cancelled) setAccess(data);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setAccess(null);
          setError(err instanceof Error ? err : new Error(String(err)));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [societyCode, sessionReady, sessionStatus]);

  const requiresLink = useMemo(() => {
    if (!access?.exists || !access.isPrivate) {
      return false;
    }

    if (!sessionReady) {
      return false;
    }

    if (!isAuthenticated) {
      return !access.canAccess;
    }

    if (linksLoading) {
      return false;
    }

    // Private access is per-society: a link to AUNPK must not grant AUNPA.
    // Staff still need a per-society i4 link (backend enforces the same rule).
    return !hasLinkForSociety;
  }, [access, sessionReady, isAuthenticated, linksLoading, hasLinkForSociety]);

  const resolvingAccess =
    loading ||
    !sessionReady ||
    (isAuthenticated && access?.isPrivate && linksLoading);

  return {
    access,
    loading: resolvingAccess,
    error,
    requiresLink,
    sessionReady,
    hasLinkForSociety,
  };
}
